import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useNespresso } from '../context/NespressoContext';
import OfferGrid from '../components/OfferGrid';
import { acceptOffer } from '../services/offerAcceptance';
import { CheckCircle } from 'lucide-react';

// Nespresso brand colors
const NESPRESSO = {
  brown: '#986f38',
  brownHover: '#815e2f',
  gold: '#c69f36',
  black: '#000000',
  darkGray: '#292929',
  lightGray: '#f9f9f9',
  border: '#e6e5e4',
};

// Confirmation state types
type ConfirmationState = 'browsing' | 'offer-accepted' | 'points-added';

const NespressoOfferView: React.FC = () => {
  const navigate = useNavigate();
  const { state } = useNespresso();
  const [selectedOfferId, setSelectedOfferId] = useState<string | null>(null);
  const [isAccepting, setIsAccepting] = useState(false);
  const [confirmationState, setConfirmationState] = useState<ConfirmationState>('browsing');
  const [acceptedOfferName, setAcceptedOfferName] = useState<string>('');

  // Points = pods (1 point per pod per spec)
  const totalPoints = state.gamification.lifetimePods;
  const capsuleCount = state.gamification.lifetimePods;

  const handleSelectOffer = (offerId: string) => {
    setSelectedOfferId(offerId);
  };

  const handleAcceptOffer = async (offerId: string) => {
    setIsAccepting(true);
    try {
      const result = await acceptOffer(offerId);
      if (result) {
        // Show confirmation that offer was accepted
        setAcceptedOfferName(result.offerName || 'Selected offer');
        setConfirmationState('offer-accepted');
      }
    } catch (error) {
      console.error('Failed to accept offer:', error);
    } finally {
      setIsAccepting(false);
    }
  };

  const handleNoThanks = () => {
    // Show confirmation that points were added
    setConfirmationState('points-added');
  };

  const handleFinish = () => {
    // Return to Step 0 (demo landing page)
    navigate('/nespresso');
  };

  const handleBack = () => {
    navigate('/nespresso/confirmation');
  };

  // Render confirmation screen
  if (confirmationState !== 'browsing') {
    return (
      <div
        className="min-h-screen flex flex-col"
        style={{ backgroundColor: NESPRESSO.black }}
      >
        <header className="py-6 px-8 flex items-center justify-center">
          <img
            src="/assets/images/logos/nespresso-white.svg"
            alt="Nespresso"
            className="h-6 w-auto"
          />
        </header>

        <main className="flex-1 flex flex-col items-center justify-center px-8">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', duration: 0.5 }}
            className="mb-8"
          >
            <div
              className="w-24 h-24 rounded-full flex items-center justify-center"
              style={{
                background: `linear-gradient(135deg, ${NESPRESSO.gold} 0%, ${NESPRESSO.brown} 100%)`,
                boxShadow: `0 10px 40px ${NESPRESSO.gold}40`,
              }}
            >
              <CheckCircle className="w-12 h-12 text-white" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-center max-w-md"
          >
            {confirmationState === 'offer-accepted' ? (
              <>
                <h1 className="text-3xl font-light text-white mb-3">
                  Offer Added!
                </h1>
                <p className="text-lg text-white/60 font-light mb-2">
                  {acceptedOfferName}
                </p>
                <p className="text-white/40 text-sm">
                  Your discount code has been saved to your Nespresso Club account.
                  Show this at checkout to redeem.
                </p>
              </>
            ) : (
              <>
                <h1 className="text-3xl font-light text-white mb-3">
                  Points Added!
                </h1>
                <p className="text-lg font-light mb-2" style={{ color: NESPRESSO.gold }}>
                  +{totalPoints} points
                </p>
                <p className="text-white/40 text-sm">
                  Your points have been added to your Nespresso Club account.
                  Use them on your next visit!
                </p>
              </>
            )}
          </motion.div>

          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            onClick={handleFinish}
            className="mt-10 px-12 py-4 font-medium text-lg transition-colors"
            style={{
              background: `linear-gradient(135deg, ${NESPRESSO.gold} 0%, ${NESPRESSO.brown} 100%)`,
              color: 'white',
              borderRadius: '3px',
            }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Done
          </motion.button>
        </main>

        <footer className="py-4 px-8 text-center text-white/30 text-xs border-t border-white/5 font-light">
          Powered by Pentatonic AI
        </footer>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: NESPRESSO.black }}
    >
      {/* Header */}
      <header className="py-4 px-8 flex items-center justify-between border-b border-white/10">
        <button
          onClick={handleBack}
          className="text-white/60 hover:text-white transition-colors flex items-center gap-2"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back
        </button>
        <div className="flex items-center gap-3">
          <span
            className="font-medium uppercase tracking-widest text-sm"
            style={{ color: NESPRESSO.gold }}
          >
            Nespresso Recycling Program
          </span>
        </div>
        <div className="text-white font-medium">
          Your Points:{' '}
          <span style={{ color: NESPRESSO.gold }}>
            {totalPoints}
          </span>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <h1 className="text-4xl font-light text-white mb-3">
            You've earned{' '}
            <span style={{ color: NESPRESSO.gold }}>
              {totalPoints} points
            </span>
          </h1>
          <p className="text-lg text-white/60 font-light">
            Redeem your points for exclusive rewards, or save them for later.
          </p>
        </motion.div>

        {/* Offer Grid */}
        <div className="mb-8">
          <OfferGrid
            selectedOfferId={selectedOfferId}
            onSelectOffer={handleSelectOffer}
            onAcceptOffer={handleAcceptOffer}
          />
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col items-center gap-4">
          {/* Finish Button - always visible */}
          <button
            onClick={handleNoThanks}
            disabled={isAccepting}
            className="px-8 py-3 bg-white/10 text-white font-medium hover:bg-white/20 transition-colors disabled:opacity-50"
            style={{ borderRadius: '3px' }}
          >
            {isAccepting ? 'Processing...' : `No Thanks, Just Add ${totalPoints} Points to My Account`}
          </button>
        </div>

        {/* Items Recycled Summary */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-8 text-center"
        >
          <p className="text-white/40 text-sm font-light">
            {capsuleCount} capsule{capsuleCount !== 1 ? 's' : ''} recycled
          </p>
        </motion.div>
      </main>
    </div>
  );
};

export default NespressoOfferView;
